import React from "react";
import MDEditor, { ICommand, TextAreaTextApi, TextState, commands } from "@uiw/react-md-editor";
import { generateDiffMarkup } from "../diff/generator";

export interface AmendmentEditorProps {
  /** The text of the item being amended — the base the mark-up draft is generated against. */
  baseText: string;
  /** The clean amendment wording (authoritative; used by the Accept flow). */
  text: string;
  onTextChange: (value: string) => void;
  /** The presentation mark-up shown to Synod. Empty = legacy computed diff. */
  markup: string;
  onMarkupChange: (value: string) => void;
  diffMode: string;
}

function wrapSelection(tag: "ins" | "del"): ICommand {
  return {
    name: `mark-${tag}`,
    keyCommand: `mark-${tag}`,
    buttonProps: { "aria-label": tag === "ins" ? "Mark selection as added" : "Mark selection as removed", title: tag === "ins" ? "Mark selection as added" : "Mark selection as removed" },
    icon:
      tag === "ins" ? (
        <span style={{ color: "var(--sl-for)", fontWeight: 700 }}>+ added</span>
      ) : (
        <span style={{ color: "var(--sl-against)", fontWeight: 700, textDecoration: "line-through" }}>removed</span>
      ),
    execute: (state: TextState, api: TextAreaTextApi) => {
      api.replaceSelection(`<${tag}>${state.selectedText}</${tag}>`);
    },
  };
}

/**
 * Two-step amendment editor:
 *  1. edit the clean amendment wording;
 *  2. author the mark-up shown to Synod (generate a draft from the changes,
 *     then correct it by hand with the added/removed buttons).
 * The mark-up is optional — without it, viewers see the automatic diff.
 */
function AmendmentEditor({ baseText, text, onTextChange, markup, onMarkupChange, diffMode }: AmendmentEditorProps) {
  function generateDraft() {
    onMarkupChange(generateDiffMarkup(baseText, text, diffMode));
  }

  return (
    <div>
      <div className="mb-1 fw-bold">1. Amendment text (the clean wording that is voted on)</div>
      <MDEditor className="mb-3" value={text} onChange={(value) => onTextChange(value ?? "")} preview="edit" height={300} />

      <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
        <span className="fw-bold">2. Mark-up shown to Synod</span>
        <button type="button" className="btn btn-light btn-sm" onClick={generateDraft} title="Generate a draft mark-up from the changes, then correct it by hand">
          Generate from changes
        </button>
        {markup ? (
          <button type="button" className="btn btn-light btn-sm" onClick={() => onMarkupChange("")}>
            Clear
          </button>
        ) : null}
      </div>
      {markup ? (
        <>
          <MDEditor
            className="mb-2"
            value={markup}
            onChange={(value) => onMarkupChange(value ?? "")}
            preview="edit"
            height={300}
            commands={[wrapSelection("ins"), wrapSelection("del"), commands.divider, commands.bold, commands.italic]}
          />
          <div className="mb-1 text-muted small">Preview (what attendees will see):</div>
          <div className="border rounded p-3 sl-prose mb-2">
            <MDEditor.Markdown source={markup} />
          </div>
        </>
      ) : (
        <p className="text-muted small">
          No mark-up authored — attendees will see an automatically generated comparison, which can render poorly for lists and formatting. Click <em>Generate from changes</em> to create a draft you
          can correct before publishing.
        </p>
      )}
    </div>
  );
}

export default AmendmentEditor;
